import React, { useState } from 'react';
import styled from 'styled-components';
import Tag from './Tag';
import { Container, Heading, Text } from '../styles/components';
import ReactPlayer from 'react-player/lazy';
import CloseButton from './CloseButton';

const zones = [
  {
    id: 1,
    title: 'Zone 1: Strategy Blueprint',
    subtitle: 'A single source of truth that drives clarity & consistency',
    content: [
      'Transform scattered strategy into organised power. Your Blueprint connects and activates all your key insights:',
      'Add, upload or even ask Moogul to generate your strategy',
      'Capture deep understanding of your audience',
      'Document your IP, frameworks and methodologies',
      'Create smart offers based on target market psychographics for maximum market fit',
      'Generate key messages, content pillars and platform strategies to build your own platforms and brand',
      'Capture your unique tone of voice and use it in all content for consistency, trust, and brand awareness'
    ],
    videoUrl: 'YOUR_VIDEO_URL_HERE', // Replace with actual video URL
  },
  {
    id: 2,
    title: 'Zone 2: Asset Hub',
    subtitle: 'Where your business knowledge becomes searchable, usable gold',
    content: [
      'Stop losing valuable content and knowledge in forgotten folders. Your Asset Hub organizes and activates everything:',
      'Store and tag all your generated content',
      'Build a library of proof - testimonials, results, and stats',
      'Import external knowledge from PDFs, URLs, and documents',
      'Link assets to items in your Strategy Blueprint'
    ],
    videoUrl: 'YOUR_VIDEO_URL_HERE', // Replace with actual video URL
  },
  {
    id: 3,
    title: 'Zone 3: Creator Express',
    subtitle: 'Never type another prompt. Never \'unlock\' or \'unleash\' a single damn thing again!',
    content: [
      'Generate unique, strategy-aligned content in seconds. No generic bot-talk. Just your business brain at lightspeed:',
      'Drive content directly from your Strategy Blueprint',
      'Generate everything from social posts to sales pages in just a few clicks',
      'Select the AI model that works best for your brand',
      'Consistent messaging, infinite variety'
    ],
    videoUrl: 'YOUR_VIDEO_URL_HERE', // Replace with actual video URL
  },
  {
    id: 4,
    title: 'Zone 4: Ask Moogul',
    subtitle: 'Your always-on strategic advisor',
    content: [
      'Chat naturally with an AI that knows - and can search - your business. Get answers, insights and ideas instantly:',
      'Ask anything about your business strategy',
      'Brainstorm, strategise, create content - anything you want!',
      'Starter prompts for the AI curious; freedom to let rip for AI legends',
      'Assign topics from your Strategy Blueprint - or just let Moogul riff based on all your knowledge',
      'Create custom output templates so you can remix your strategy into your favourite format',
      'Access all your favourite AI models'
    ],
    videoUrl: 'YOUR_VIDEO_URL_HERE', // Replace with actual video URL
  }
];

const SectionWrapper = styled.section`
  min-height: calc(100svh - 80px); /* 80px is header height */
  background: ${props => props.theme.colors.background};
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: ${props => props.theme.spacing.xxl} 0;
`;

const ContentContainer = styled(Container)`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${props => props.theme.spacing.xl};
  flex: 1;
  margin-top: ${props => props.theme.spacing.xl};
  align-items: stretch;
`;

const Card = styled.div`
  background: ${props => props.theme.colors.backgroundAlt};
  border-radius: ${props => props.theme.radii.card};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.default};
  border: 1px solid ${props => props.theme.colors.border};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.shadows.lg};

    .card-image {
      transform: scale(1.05);
    }

    .see-more {
      background: ${props => props.theme.colors.brand};
      color: ${props => props.theme.colors.text};
    }
  }
`;

const CardImage = styled.div`
  height: 200px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  transition: transform ${props => props.theme.transitions.default};
`;

const CardContent = styled.div`
  padding: ${props => props.theme.spacing.lg};
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.h3`
  color: ${props => props.theme.colors.text};
  font-family: ${props => props.theme.typography.heading.fontFamily};
  font-size: ${props => props.theme.typography.sizes.h4};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const CardSubtitle = styled.p`
  color: ${props => props.theme.colors.textLight};
  font-size: ${props => props.theme.typography.sizes.bodySmall};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const SeeMore = styled.div`
  background: ${props => props.theme.colors.backgroundAlt};
  color: ${props => props.theme.colors.textLight};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.radii.round};
  font-size: ${props => props.theme.typography.sizes.bodySmall};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  transition: all ${props => props.theme.transitions.default};
  margin-top: auto;

  &::after {
    content: "→";
    font-size: 1.2em;
    transition: transform ${props => props.theme.transitions.default};
  }

  ${Card}:hover &::after {
    transform: translateX(4px);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(16, 69, 81, 0.95);
  z-index: 999;
`;

const ExpandedCard = styled.div`
  position: fixed;
  top: 150px; /* Header height */
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 1000px;
  height: calc(100vh - 200px); /* Account for header height + some padding */
  background: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.radii.lg};
  padding: ${props => props.theme.spacing.xl};
  z-index: 1000;
  overflow-y: auto;
  box-shadow: ${props => props.theme.shadows.lg};
  
  /* Smooth scrolling */
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;

  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.backgroundAlt};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.brand};
    border-radius: 4px;
  }

  .video-wrapper {
    position: relative;
    padding-top: 56.25%; /* 16:9 Aspect Ratio */
    margin-bottom: ${props => props.theme.spacing.xl};
    background: black;
    border-radius: ${props => props.theme.radii.sm};
    overflow: hidden;
  }

  .react-player {
    position: absolute;
    top: 0;
    left: 0;
  }

  /* Add some responsive adjustments */
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    top: 60px;
    height: calc(100vh - 80px);
    padding: ${props => props.theme.spacing.lg};
  }
`;

const BulletList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: ${props => props.theme.spacing.lg} 0;

  li {
    position: relative;
    padding-left: ${props => props.theme.spacing.xl};
    margin-bottom: ${props => props.theme.spacing.md};
    line-height: ${props => props.theme.typography.lineHeights.body};
    color: ${props => props.theme.colors.textLight};

    &:before {
      content: "•";
      color: ${props => props.theme.colors.secondary};
      position: absolute;
      left: 0;
      font-size: 1.5em;
      line-height: 1;
    }

    &:first-child {
      font-weight: 600;
      color: ${props => props.theme.colors.text};
      margin-bottom: ${props => props.theme.spacing.lg};
    }
  }
`;

const StrategyZonesSection = () => {
  const [expandedZone, setExpandedZone] = useState(null);

  const handleCardClick = (zone) => {
    setExpandedZone(zone);
  };

  const handleClose = () => {
    setExpandedZone(null);
  };

  return (
    <SectionWrapper>
      <ContentContainer>
        <Tag>How a Strategy Studio works</Tag>
        <Heading as="h2" $maxWidth="800px">
          Four business-boosting zones.
          <br />
          One superintelligent space cow.
          <br />
          Infinite growth potential.
        </Heading>

        <GridContainer>
          {zones.map((zone) => (
            <Card key={zone.id} onClick={() => handleCardClick(zone)}>
              <CardImage 
                className="card-image"
                image={`/assets/zones/zone-${zone.id}.webp`} 
                role="img"
                aria-label={`Illustration for ${zone.title}`}
              />
              <CardContent>
                <CardTitle>{zone.title}</CardTitle>
                <CardSubtitle>{zone.subtitle}</CardSubtitle>
                <SeeMore className="see-more">See how it works</SeeMore>
              </CardContent>
            </Card>
          ))}
        </GridContainer>

        {expandedZone && (
          <>
            <Overlay onClick={handleClose} />
            <ExpandedCard>
              <CloseButton 
                onClick={handleClose}
                position="topRightSticky"
                variant="transparent"
                rotate
              />
              <div className="video-wrapper">
                <ReactPlayer
                  url={expandedZone.videoUrl}
                  className="react-player"
                  width="100%"
                  height="100%"
                  controls
                  config={{
                    file: {
                      forceHLS: true,
                      hlsOptions: {
                        maxLoadingDelay: 4,
                        minAutoBitrate: 0,
                        lowLatencyMode: true
                      }
                    }
                  }}
                />
              </div>
              <Heading as="h3">{expandedZone.title}</Heading>
              <Text size="large" color="secondary">{expandedZone.subtitle}</Text>
              <BulletList>
                {expandedZone.content.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </BulletList>
            </ExpandedCard>
          </>
        )}
      </ContentContainer>
    </SectionWrapper>
  );
};

export default StrategyZonesSection;